import type { ReactNode } from "react";
import { SkeletonRows } from "./Skeleton";

export interface ReportColumn<T> {
  key: string;
  label: string;
  align?: "left" | "right";
  render?: (row: T) => ReactNode;
}

/** Generic read-only report/audit-log table — shared markup only (no
 * fetching, no selection, no row-click), matching the boilerplate that was
 * already copy-pasted across PoResult/PoProposals/SlaResult/FirmSlaResult. */
export function ReportTable<T>({
  columns,
  rows,
  rowKey,
  loading,
  emptyMessage = "No records found.",
}: {
  columns: ReportColumn<T>[];
  rows: T[];
  rowKey: (row: T) => string | number;
  loading?: boolean;
  emptyMessage?: string;
}) {
  return (
    <div className="overflow-x-auto">
      <table className="data w-full text-xs">
        <thead className="bg-slate-50 dark:bg-white/5 text-slate-500">
          <tr>
            {columns.map((c) => (
              <th key={c.key} className={`p-3 font-semibold uppercase tracking-wide text-[10px] ${c.align === "right" ? "text-right" : "text-left"}`}>
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <SkeletonRows columns={columns.length} />
          ) : rows.length ? (
            rows.map((row) => (
              <tr key={rowKey(row)} className="border-t border-slate-100 dark:border-white/5 hover:bg-slate-50 dark:hover:bg-white/5">
                {columns.map((c) => (
                  <td key={c.key} className={`p-3 ${c.align === "right" ? "text-right" : "text-left"}`}>
                    {c.render ? c.render(row) : String((row as Record<string, unknown>)[c.key] ?? "—")}
                  </td>
                ))}
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={columns.length} className="p-8 text-center text-slate-400">
                {emptyMessage}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
